import { useContext, useEffect, useMemo, useRef, useState } from 'react'

import { serviceIDs } from 'utils/constants/services'
import Link from 'utils/navigation/CustomLink'
import useNavigate from 'utils/navigation/useCustomNavigate'
import useServices from 'views/hooks/useServices'
import { AppContext } from '../../../providers/context/App'
import EstheticianListProvider from '../../../providers/context/EstheticianList'
import {
  ENHANCEMENT_ROUTE,
  FACIALS_ROUTE,
  HYDRAFACIAL_IDS,
  LOCATIONS_ROUTE,
} from '../../../utils/constants/Helpers'
import { getBannerText, getFacialType } from '../../../utils/helper-functions'
import { HeaderTheme } from '../../../utils/types/appTypes'
import { RefClickObject } from '../../../utils/types/estheticianTypes'
import CalendarView from '../../components/calendar-view'
import CommonHeader from '../../components/common-header'
import EstheticianTimeslots from '../../components/esthetician-timeslots'
import Filter from '../../components/filter'

import { ReactComponent as CloseIcon } from '../../../assets/icons/banner-close.svg'
import { ReactComponent as MapPinIcon } from '../../../assets/icons/map-pin-1-new.svg'
import SpiralCalendar from '../../../assets/images/spiral_calendar.png'
import './estheticianList.scss'

const EstheticianList = () => {
  const { appMasterData } = useContext(AppContext)
  const childRef = useRef<RefClickObject>(null)

  const { isInfluencerFlow, navigate } = useNavigate()
  const { facialName } = useServices()

  const [bannerText, setBannerText] = useState('')
  const [isBannerShow, setIsBannerShow] = useState(false)
  const [isAnimationShow, setIsAnimationShow] = useState(false)
  const [isFilterShow, setIsFilterShow] = useState(true)

  const {
    location,
    category,
    selectedEnhancements = [],
    availableEnhancements = [],
    isLaserFacial,
    isMicroNeedling,
  } = appMasterData

  const isDermaplaning = useMemo(
    () => selectedEnhancements.some((s) => s.name === 'Dermaplaning'),
    [selectedEnhancements]
  )

  const isHydrafacial = useMemo(
    () => HYDRAFACIAL_IDS.includes(category.categoryId),
    [category.categoryId]
  )

  const isFullEditFacial =
    serviceIDs?.[6] === appMasterData?.category?.categoryId

  const shouldHideFilter = location.enableGhostBooking || isDermaplaning

  useEffect(() => {
    if (!location.locationId) {
      navigate(LOCATIONS_ROUTE)
      return
    }

    const banner = getBannerText(location)
    if (banner) {
      setBannerText(banner)
      setIsBannerShow(true)
      setIsAnimationShow(true)
    }

    if (shouldHideFilter) {
      setIsFilterShow(false)
    }
  }, [location, navigate, shouldHideFilter])

  const openFilter = () => {
    childRef.current?.handleFilterBtnClick()
  }

  const handleBack = () => {
    const shouldGoToFacials =
      isLaserFacial || isMicroNeedling || isHydrafacial || isFullEditFacial

    const route = shouldGoToFacials ? FACIALS_ROUTE : ENHANCEMENT_ROUTE

    navigate(availableEnhancements.length ? route : LOCATIONS_ROUTE)
  }

  const handleOnFinish = () => {
    setIsAnimationShow(true)
  }

  const renderLocationName = () => {
    const name = location.name

    if (isLaserFacial) {
      return <span className="sub-head laser-sub-head">{name}</span>
    }

    return (
      <Link to={LOCATIONS_ROUTE} className="sub-head">
        {name}
      </Link>
    )
  }

  return (
    <EstheticianListProvider>
      <div className="esth-list-container">
        <CommonHeader backToFunction={handleBack} theme={HeaderTheme.ORANGE} />
        <div className="est-list-content">
          <div className="select-location">
            <p className="selected-facial" data-testid="selected-facial">
              {isInfluencerFlow ? facialName : getFacialType(appMasterData)} at
            </p>
            <p className="location-name">
              <MapPinIcon className="map-icon" />
              {renderLocationName()}
            </p>
          </div>
          {isFilterShow && <Filter ref={childRef} />}
          <CalendarView />
        </div>
        <EstheticianTimeslots
          openFilter={openFilter}
          onFinish={handleOnFinish}
          showFilter={isFilterShow}
        />
      </div>

      {isBannerShow && isAnimationShow && (
        <div>
          <div
            className="offer-banner cssanimation fadeInBottom"
            data-testid="banner"
          >
            <div className="massage">
              {bannerText}{' '}
              <img
                src={SpiralCalendar}
                alt="spiral_calender"
                className="calender-icon"
                loading="lazy"
              />
            </div>
            <CloseIcon
              onClick={() => setIsBannerShow(false)}
              data-testid="close banner"
            />
          </div>
        </div>
      )}
    </EstheticianListProvider>
  )
}

export default EstheticianList
